import { useCallback, useContext, useEffect, useMemo, useRef, useState, type MutableRefObject, type RefObject } from 'react';
import { useLocation } from 'react-router-dom';

import NoDataForSelection from 'components/atoms/NoDataForSelection';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import PackArchitectureChartWithControls from 'components/molecules/PackArchitecture/ReusableBubbleChart';
import PackArchitectureTable from 'components/molecules/PackArchitecture/PackArchitectureTable';
import { getPackArchitectureEntityKey } from 'components/molecules/PackArchitecture/packArchitectureTable.helpers';
import { usePackArchitectureChartToTableScrollSync } from 'components/molecules/PackArchitecture/usePackArchitectureChartToTableScrollSync';
import {
    collectConfiguredPackArchitectureKpiIds,
    createPackArchitectureKpiIdMatcher,
} from 'components/pages/PackArchitecture/packArchitectureKpiIdMatching.helpers';
import { usePackArchitectureContext } from 'contexts/PackArchitectureContext';
import { UserContext } from 'contexts/ConfigContext';
import { DIAGNOSTIC_PAGE_NAME } from 'components/molecules/PackArchitecture/axisConfigurationModal.constants';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PPA_PACK_ARCH_EXPORT_CLICKED, RGM_COPY_CLICKED, RGM_COPY_EXECUTED } from 'utils/mixpanelEventNames';
import { getConfig, getScreenInfoFromUrl } from 'utils/helperMethods';

import {
    AXIS_PREVIEW_SALES_KPI_IDS,
    buildPackArchitectureChartTitle,
    buildPackArchitectureChartTitleSegments,
    getAxisLabel,
    getAxisLabelWithSuffix,
    type PackArchitectureChartTitleInput,
} from './packArchitectureMeta.helpers';
import PackArchitectureExportModal from './PackArchitectureExportModal';
import { PackArchitectureBreadcrumb, PackArchitecturePageHeader } from './PackArchitecturePageHeader';
import PackArchitectureToolbar from './PackArchitectureToolbar';
import { usePackArchitectureExport } from './usePackArchitectureExport';
import { usePackArchitecturePageTracking } from './usePackArchitecturePageTracking';
import {
    PPA_PACK_ARCH_CHART,
    PPA_PACK_ARCH_CONTENT,
    PPA_PACK_ARCH_NO_DATA,
    PPA_PACK_ARCH_PAGE,
} from 'constants/ppaTestIds';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';

const packArchTableContainerId = 'pack-architecture-table-container';
const CHART_VIEWPORT_HEIGHT_RATIO = 0.6;
const TABLE_VIEWPORT_HEIGHT_RATIO = 0.4;
const DEFAULT_CHART_MIN_HEIGHT_PX = 416;
const DEFAULT_TABLE_MIN_HEIGHT_PX = 252;

const PACK_ARCH_COPY_PAGE_NAME = DIAGNOSTIC_PAGE_NAME.ppa;
const PACK_ARCH_COPY_SECTION = 'chart';

const ChartTitle = ({chartCopyRef}: {chartCopyRef: RefObject<HTMLElement>}) => {
    const chartTitleInput = usePackArchitectureChartTitleInput();
    const titleSegments = useMemo(() => buildPackArchitectureChartTitleSegments(chartTitleInput), [chartTitleInput]);
    const chartName = useMemo(() => buildPackArchitectureChartTitle(chartTitleInput), [chartTitleInput]);

    const copyTrackingProps = useMemo(
        () => ({
            page_name: PACK_ARCH_COPY_PAGE_NAME,
            section: PACK_ARCH_COPY_SECTION,
            chart_name: chartName,
        }),
        [chartName],
    );

    const handleCopyClicked = useCallback(() => {
        trackEventMixpanel(RGM_COPY_CLICKED, copyTrackingProps);
    }, [copyTrackingProps]);

    const handleCopyExecuted = useCallback(
        (withFilters: boolean) => {
            trackEventMixpanel(RGM_COPY_EXECUTED, {
                ...copyTrackingProps,
                method: withFilters ? 'chart_with_filters' : 'chart_only',
            });
        },
        [copyTrackingProps],
    );

    return (
        <div className="flex items-center justify-between">
            <div>
            {titleSegments.map((segment, index) =>
                segment.emphasis ? (
                    <span key={`${segment.text}-${index}`} className="font-semibold">
                        {segment.text}
                    </span>
                ) : (
                    <span key={`${segment.text}-${index}`}>{segment.text}</span>
                ),
            )}
            </div>
            <div className="flex justify-end">
                <CopyToClipBoard
                    refOfComponent={chartCopyRef}
                    mixpanelEvent={handleCopyClicked}
                    onCopyExecuted={handleCopyExecuted}
                />
            </div>
        </div>
    );
};

const usePackArchitectureChartTitleInput = (): PackArchitectureChartTitleInput => {
    const { xAxis, xAxisConfig, xAxisOptionGroups, yAxis, yAxisOptionGroups, bubbleRepresents, bubbleRepresentsOptionGroups } =
        usePackArchitectureContext();

    return useMemo(
        () => ({
            xAxis,
            xAxisConfig,
            xAxisOptionGroups,
            yAxis,
            yAxisOptionGroups,
            bubbleRepresents,
            bubbleRepresentsOptionGroups,
        }),
        [xAxis, xAxisConfig, xAxisOptionGroups, yAxis, yAxisOptionGroups, bubbleRepresents, bubbleRepresentsOptionGroups],
    );
};

// @code-guard: skip-next-function reason="Legacy page container orchestrates chart/table sync and will be split into hooks/components in follow-up."
const PackArchitecture = () => {
    const { appConfig } = useContext(UserContext);
    const { pathname } = useLocation();
    const {
        title,
        tooltip,
        xAxis,
        xAxisOptionGroups,
        yAxis,
        yAxisOptionGroups,
        bubbleRepresents,
        bubbleRepresentsOptionGroups,
        bubbleSize,
        bubbleSizeOptionGroups,
        showHeatMap,
        tableKpiOptionItems,
        rows,
        chartRequest,
        totalCount,
        hasChartData,
        isChartPanelLoading,
        showNoFilterSelection,
        clearSelection,
        tableRows,
        isChartPaginatedLoading,
        isTablePaginatedLoading,
    } = usePackArchitectureContext();
    const screensConfig = useMemo(() => getConfig('screens', appConfig) ?? [], [appConfig]);
    const [viewportHeightPx, setViewportHeightPx] = useState<number>(
        typeof window !== 'undefined' ? window.innerHeight : 0,
    );
    const tableHeight = useRef<number | null>(0);
    tableHeight.current = useFillHeightToFitScreen({
        id: packArchTableContainerId,
        // force recalculation after panel loading settles and layout mounts
        height: isChartPanelLoading ? 0 : tableHeight.current,
        buffer: 24,
        triggerDummyRender: true,
    });
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const updateViewportHeight = () => setViewportHeightPx(window.innerHeight);
        window.addEventListener('resize', updateViewportHeight);
        return () => window.removeEventListener('resize', updateViewportHeight);
    }, []);
    const chartMinHeightPx = useMemo(
        () =>
            Math.max(
                DEFAULT_CHART_MIN_HEIGHT_PX,
                Math.round((viewportHeightPx || DEFAULT_CHART_MIN_HEIGHT_PX) * CHART_VIEWPORT_HEIGHT_RATIO),
            ),
        [viewportHeightPx],
    );
    const tableMinHeightPx = useMemo(
        () =>
            Math.max(
                DEFAULT_TABLE_MIN_HEIGHT_PX,
                Math.round((viewportHeightPx || DEFAULT_TABLE_MIN_HEIGHT_PX) * TABLE_VIEWPORT_HEIGHT_RATIO),
            ),
        [viewportHeightPx],
    );
    const resolvedTableHeightPx = (() => {
        const measuredTableHeight =
            typeof tableHeight.current === 'number' && Number.isFinite(tableHeight.current) ? tableHeight.current : 0;
        return Math.max(tableMinHeightPx, measuredTableHeight);
    })();
    const breadcrumbGroupLabel = useMemo(
        () => getScreenInfoFromUrl(pathname, screensConfig).parentLabel || 'Opportunity Assessment',
        [pathname, screensConfig],
    );

    usePackArchitecturePageTracking();

    const chartHorizontalScrollRef = useRef<HTMLDivElement | null>(null);
    const tableWrapperRef = useRef<HTMLDivElement | null>(null);
    const isSyncingTableFromChartRef = useRef(false);
    const isSyncingChartFromTableRef = useRef(false);
    /** Table column pagination shares chart data — block chart→table snap until table restores scroll. */
    const suppressChartToTableSyncRef = useRef(false);

    const hasTableColumns = tableRows.length > 0;
    const chartColumnKeys = useMemo(() => rows.map((row) => getPackArchitectureEntityKey(row)), [rows]);
    const tableColumnKeys = useMemo(
        () => tableRows.map((row) => getPackArchitectureEntityKey(row)),
        [tableRows],
    );

    usePackArchitectureChartToTableScrollSync({
        chartScrollRef: chartHorizontalScrollRef,
        tableWrapperRef,
        isSyncingTableFromChartRef,
        isSyncingChartFromTableRef,
        suppressChartToTableSyncRef,
        chartColumnCount: rows.length,
        chartColumnKeys,
        isChartPaginatedLoading,
        isTablePaginatedLoading,
        tableLoadedColumnCount: tableRows.length,
        tableColumnKeys,
        // Re-sync when chart columns change or table first mounts — not on table column pagination.
        tableSyncKey: rows.length + (hasTableColumns ? 1 : 0),
    });

    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const interactivePanelRef = useRef<HTMLDivElement>(null);
    const chartAreaRef = useRef<HTMLDivElement>(null);
    const chartCopyRef = useRef<HTMLDivElement | null>(null);
    const tableContainerRef = useRef<HTMLDivElement | null>(null) as MutableRefObject<HTMLDivElement | null>;

    const xAxisLabel = useMemo(() => getAxisLabel(xAxis, xAxisOptionGroups), [xAxis, xAxisOptionGroups]);
    const yAxisLabel = useMemo(() => getAxisLabel(yAxis, yAxisOptionGroups), [yAxis, yAxisOptionGroups]);
    const bubbleRepresentsLabel = useMemo(
        () => getAxisLabel(bubbleRepresents, bubbleRepresentsOptionGroups),
        [bubbleRepresents, bubbleRepresentsOptionGroups],
    );
    const bubbleSizeLabel = useMemo(
        () => getAxisLabel(bubbleSize, bubbleSizeOptionGroups),
        [bubbleSize, bubbleSizeOptionGroups],
    );
    /** Every "Size" KPI option (Value/Volume/Unit Sales, and any added later) — drives export columns automatically. */
    const sizeKpiOptions = useMemo(
        () => bubbleSizeOptionGroups.flatMap((group) => group.options),
        [bubbleSizeOptionGroups],
    );

    const chartTitleInput = usePackArchitectureChartTitleInput();
    const chartTitle = useMemo(() => buildPackArchitectureChartTitle(chartTitleInput), [chartTitleInput]);

    const kpiIdMatcher = useMemo(
        () =>
            createPackArchitectureKpiIdMatcher({
                configuredKpiIds: collectConfiguredPackArchitectureKpiIds(
                    yAxisOptionGroups,
                    tableKpiOptionItems,
                    AXIS_PREVIEW_SALES_KPI_IDS,
                    bubbleSizeOptionGroups,
                ),
                salesKpiIds: AXIS_PREVIEW_SALES_KPI_IDS,
            }),
        [bubbleSizeOptionGroups, tableKpiOptionItems, yAxisOptionGroups],
    );

    const { isExporting, handleExport } = usePackArchitectureExport({
        chartTitle,
        xAxisLabel,
        yAxis,
        yAxisLabel,
        bubbleRepresentsLabel,
        bubbleSizeLabel,
        sizeKpiOptions,
        showHeatMap,
        kpiIdMatcher,
        rows,
        chartRequest,
        totalCount,
        onExportSuccess: () => setIsExportModalOpen(false),
    });

    const handleOpenExportModal = useCallback(() => {
        trackEventMixpanel(PPA_PACK_ARCH_EXPORT_CLICKED, {
            x_axis: xAxis,
            y_axis: yAxis,
            bubble_represents: bubbleRepresents,
            bubble_size: bubbleSize,
        });
        setIsExportModalOpen(true);
    }, [bubbleRepresents, bubbleSize, xAxis, yAxis]);
    const handleCloseExportModal = useCallback(() => {
        if (isExporting) return;
        setIsExportModalOpen(false);
    }, [isExporting]);

    useEffect(() => {
        const handleOutsidePointerDown = (event: MouseEvent) => {
            if (interactivePanelRef.current && !interactivePanelRef.current.contains(event.target as Node)) {
                clearSelection();
            }
        };
        document.addEventListener('mousedown', handleOutsidePointerDown);
        return () => document.removeEventListener('mousedown', handleOutsidePointerDown);
    }, [clearSelection]);

    const handleInteractivePanelClick = useCallback(
        (event: React.MouseEvent) => {
            const target = event.target as Node;
            if (tableContainerRef.current?.contains(target)) return;
            if (chartAreaRef.current?.contains(target)) return;
            clearSelection();
        },
        [clearSelection],
    );

    return (
        <div className="flex min-w-0 flex-col gap-2 overflow-x-clip py-0" data-testid={PPA_PACK_ARCH_PAGE}>
            <PackArchitectureBreadcrumb title={title} groupLabel={breadcrumbGroupLabel} />

            <div
                ref={interactivePanelRef}
                className="rounded-xl bg-white p-4"
                data-testid={PPA_PACK_ARCH_CONTENT}
                onClick={handleInteractivePanelClick}
            >
                <PackArchitecturePageHeader
                    title={title}
                    tooltip={tooltip}
                    onDownloadClick={handleOpenExportModal}
                    downloadDisabled={!hasChartData || isChartPanelLoading || showNoFilterSelection}
                    isDownloadOpen={isExportModalOpen}
                />

                {showNoFilterSelection ? (
                    <div
                        className="flex min-h-[420px] items-center justify-center rounded-md border border-borderLinePurpuleAlt bg-white py-12"
                        data-testid={PPA_PACK_ARCH_NO_DATA}
                    >
                        <NoDataForSelection />
                    </div>
                ) : (
                    <>
                        <PackArchitectureToolbar />

                        <div ref={chartAreaRef} data-testid={PPA_PACK_ARCH_CHART} className="relative">
                            <div ref={chartCopyRef}>
                                <PackArchitectureChartWithControls
                                    chartTitle={<ChartTitle chartCopyRef={chartCopyRef} />}
                                    xAxisTitle={getAxisLabelWithSuffix(xAxis, xAxisOptionGroups)}
                                    yAxisTitle={getAxisLabelWithSuffix(yAxis, yAxisOptionGroups)}
                                    chartMinHeightPx={chartMinHeightPx}
                                    horizontalScrollRef={chartHorizontalScrollRef}
                                    isSyncingChartFromTableRef={isSyncingChartFromTableRef}
                                />
                            </div>
                        </div>

                        <div id={packArchTableContainerId}>
                            <PackArchitectureTable
                                tableContainerRef={tableContainerRef}
                                tableWrapperRef={tableWrapperRef}
                                isSyncingTableFromChartRef={isSyncingTableFromChartRef}
                                suppressChartToTableSyncRef={suppressChartToTableSyncRef}
                                tableScrollY={resolvedTableHeightPx}
                            />
                        </div>
                    </>
                )}
            </div>

            <PackArchitectureExportModal
                open={isExportModalOpen}
                exporting={isExporting}
                noData={!hasChartData}
                rowCount={totalCount}
                onClose={handleCloseExportModal}
                onExport={handleExport}
            />
        </div>
    );
};

export default PackArchitecture;
